import React, { useMemo } from 'react';
import { MeshBasicMaterial, SphereGeometry } from 'three';

import { AnalogJoint } from '@paragon/contract-library';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getObjectCapacityColor } from '../../store/selectors/getObjectCapacityColor';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { getShouldShowLabel } from '../../store/selectors/viewStatusSelectors';
import { LabelVisual } from '../util/LabelVisual';

const defaultMaterial = new MeshBasicMaterial({ color: 0xf26035 });
const selectedMaterial = new MeshBasicMaterial({ color: 0x3ce74c });

const jointGeometry = new SphereGeometry(0.5, 8, 8);

export function AnalogJointVisual({ analogJoint }: { analogJoint: AnalogJoint }) {
  const dispatch = useAppDispatch();

  const shouldShowLabel = useAppSelector(getShouldShowLabel);
  const labelVisible = useMemo(
    () => shouldShowLabel(analogJoint.guid, 'analogJoint'),
    [shouldShowLabel, analogJoint.guid],
  );

  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  const selected = selectedEntry?.type === 'analogJoint' && selectedEntry.item.guid === analogJoint.guid;

  const colorFunc = useAppSelector(getObjectCapacityColor);
  const color = useMemo(
    () => colorFunc({ itemGuid: analogJoint.guid, itemType: 'analogJoint' }),
    [colorFunc, analogJoint.guid],
  );

  const material = selected ? selectedMaterial : color ? new MeshBasicMaterial({ color }) : defaultMaterial;

  const item = { type: 'analogJoint', guid: analogJoint.guid, origin: 'fiber' } as const;

  const onClick = useClickItem({ type: 'analogJoint', item: analogJoint });

  return (
    <mesh
      onClick={onClick}
      onPointerOver={(event) => {
        event.stopPropagation();
        dispatch(ensureHoverItemIsActive(item));
      }}
      onPointerOut={() => dispatch(clearHoverItemIfActive(item))}
      userData-raycastOrder={RaycastOrder.AnalogJointVisual}
      geometry={jointGeometry}
      position={[analogJoint.x, analogJoint.y, analogJoint.z]}
      material={material}
      renderOrder={RenderOrder.AnalogJointVisual}
    >
      {labelVisible ? <LabelVisual fontSize={2}>{analogJoint.name}</LabelVisual> : null}
    </mesh>
  );
}
